// Sidebar, drawn by AppShell (MGMT-PANEL-BUILD-SHELL-01, direction A).
// Kept exactly from the previous Sidebar.js: the two groups and their order, the NEW badges, the Leads
// and Preferences counts (same two GETs, same numbers), the Pricing role gate, the City Manager rule
// for Overview, and Logout. New: the active item is tinted with a 3px accent bar in both groups, and
// matching ignores case so /BookingDashboard highlights.
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useEmployee } from '../../contexts/EmployeeContext';
import { usePricingAccess } from '../../hooks/usePricingAccess';
import { FONT_DISPLAY, FONT_MONO } from '../../theme/united';

export const SIDEBAR_WIDTH = 248;

export const ENTERPRISE_AI_ITEMS = [
    { to: '/genesis', label: 'Genesis', badge: 'NEW' },
    { to: '/agent-builder', label: 'Agent Builder', badge: 'NEW' },
    { to: '/transcripts', label: 'Transcripts', badge: 'NEW' },
    { to: '/outbound', label: 'GTM', badge: 'NEW' },
    { to: '/inbound-leads', label: 'Leads', badge: 'NEW' },
    { to: '/memory', label: 'Memory' },
    { to: '/eval', label: 'Eval Harness' },
    { to: '/enterprises', label: 'Enterprise' },
    { to: '/money', label: 'Money' },
    { to: '/united-pricing', label: 'Pricing', badge: 'NEW', requiresPricingRole: true },
];

// Workspace items in their existing order (the Enterprise item lives in Enterprise AI above).
export const WORKSPACE_ITEMS = [
    { to: '/whook', label: 'Whook' },
    { to: '/dashboard', label: 'Overview', hideIfCityManager: true },
    { to: '/BookingDashboard', label: 'Dashboard' },
    { to: '/leads', label: 'Leads', countKey: 'leads' },
    { to: '/preferences', label: 'Preferences', countKey: 'preferences' },
    { to: '/outreach', label: 'Outreach' },
    { to: '/waitlist', label: 'Waitlist' },
    { to: '/athletes', label: 'Users' },
    { to: '/coaches', label: 'Instructors' },
    { to: '/sitemanagers', label: 'Site staff' },
    { to: '/couriers', label: 'Couriers' },
    { to: '/employees', label: 'Employees' },
    { to: '/applicants', label: 'Applicants' },
    { to: '/facilities/countries', label: 'Facilities' },
    { to: '/markets', label: 'Markets' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/sessions', label: 'Sessions' },
    { to: '/financials', label: 'Financials' },
    { to: '/contentfeed', label: 'Content Feed' },
    { to: '/marketing', label: 'Marketing' },
];

export const CITY_MANAGER_TITLES = ['City Manager I', 'City Manager II', 'City Manager III'];

export const isNavItemVisible = (item, { isCityManager, pricingAccess }) => {
    if (item.hideIfCityManager && isCityManager) return false;
    if (item.requiresPricingRole && pricingAccess !== 'allowed') return false;
    return true;
};

export const isNavItemActive = (pathname, to) => {
    const p = (pathname || '').toLowerCase();
    const t = to.toLowerCase();
    return p === t || p.startsWith(t + '/');
};

const css = `
.u-side{width:${SIDEBAR_WIDTH}px;flex:none;height:100vh;overflow-y:auto;background:var(--side);border-right:1px solid var(--line);padding:18px 12px 24px;scrollbar-width:thin;display:flex;flex-direction:column}
.u-logo{display:flex;align-items:center;gap:10px;height:44px;padding:0 12px;margin-bottom:18px;font:600 18px/1 ${FONT_DISPLAY};letter-spacing:-.01em;text-decoration:none;color:var(--ink)}
.u-logo i{width:20px;height:20px;border-radius:6px;background:var(--accent);display:block;position:relative}
.u-logo i::after{content:'';position:absolute;inset:6px;border-radius:2px;background:var(--side)}
.u-logo .e{font:500 10px/1 ${FONT_MONO};letter-spacing:.14em;color:var(--muted);margin-left:auto}
.u-group{margin:0 0 22px}
.u-group .eb{display:block;padding:0 14px 8px;font:500 11px/1.3 ${FONT_MONO};letter-spacing:.12em;text-transform:uppercase;color:var(--muted)}
.u-nav a,.u-nav button{display:flex;align-items:center;width:100%;height:40px;padding:0 12px 0 14px;border:0;border-radius:8px;background:transparent;color:var(--muted);text-decoration:none;position:relative;font:400 14.5px/1 inherit;font-family:inherit;cursor:pointer;text-align:left;transition:color .2s,background .2s}
.u-nav a:hover,.u-nav button:hover{color:var(--ink);background:var(--hover)}
.u-nav a.on{color:var(--ink);background:var(--accent-tint);font-weight:500}
.u-nav a.on::before{content:'';position:absolute;left:0;top:9px;bottom:9px;width:3px;border-radius:0 2px 2px 0;background:var(--accent)}
.u-nav .new{margin-left:auto;font:500 10px/1 ${FONT_MONO};letter-spacing:.12em;color:var(--accent-text)}
.u-nav .cnt{margin-left:auto;font:500 11px/1 ${FONT_MONO};color:var(--muted);font-variant-numeric:tabular-nums}
.u-side .u-foot-nav{margin-top:auto;padding-top:6px;border-top:1px solid var(--line)}
`;

const Sidebar = ({ handleLogout, onNavigate, id = 'panel-sidebar', className = '' }) => {
    const [totalLeads, setTotalLeads] = useState(0);
    const [totalPreferences, setTotalPreferences] = useState(0);
    const { employee } = useEmployee() || {};
    // T6-PRICING-PANEL-01: the Pricing tab shows only once mgmt-backend's gate lets this staff member through.
    const pricingAccess = usePricingAccess();
    const location = useLocation();

    const isCityManager = CITY_MANAGER_TITLES.includes(employee?.title);

    useEffect(() => {
        const fetchTotalPreferences = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/preferences/count`);
                setTotalPreferences(response.data.count);
            } catch (error) {
                console.error("❌ Error fetching preferences:", error);
            }
        };
        fetchTotalPreferences();
    }, []);

    useEffect(() => {
        const fetchTotalLeads = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leads`);
                setTotalLeads(response.data.length);
            } catch (error) {
                console.error("❌ Error fetching leads:", error);
            }
        };
        fetchTotalLeads();
    }, []);

    const counts = {
        // Same numbers as before (the +200 is PD1 dummy item; B decides it separately).
        leads: totalLeads > 0 ? totalLeads + 200 : 0,
        preferences: (totalPreferences || 0).toLocaleString(),
    };

    const renderNavItem = (item) => {
        if (!isNavItemVisible(item, { isCityManager, pricingAccess })) return null;
        const active = isNavItemActive(location.pathname, item.to);
        return (
            <Link
                key={item.to}
                to={item.to}
                className={active ? 'on' : undefined}
                aria-current={active ? 'page' : undefined}
                onClick={onNavigate}
                data-nav-item={item.label}
            >
                {item.label}
                {item.badge && <span className="new">{item.badge}</span>}
                {item.countKey && <span className="cnt" data-count={item.countKey}>{counts[item.countKey]}</span>}
            </Link>
        );
    };

    return (
        <aside id={id} className={`u-side ${className}`} aria-label="Main" data-shell-sidebar>
            <style>{css}</style>
            <div className="u-logo"><i />United<span className="e">ADMIN</span></div>

            <div className="u-group" data-nav-group="Enterprise AI">
                <span className="eb">Enterprise AI</span>
                <nav className="u-nav">{ENTERPRISE_AI_ITEMS.map(renderNavItem)}</nav>
            </div>

            <div className="u-group" data-nav-group="Workspace">
                <span className="eb">Workspace</span>
                <nav className="u-nav">{WORKSPACE_ITEMS.map(renderNavItem)}</nav>
            </div>

            <nav className="u-nav u-foot-nav">
                <button type="button" onClick={handleLogout} data-nav-item="Logout">Logout</button>
            </nav>
        </aside>
    );
};

export default Sidebar;
